/**
 * One stat read per surface, keyed on (league, range, roster).
 *
 * ── THE TWO-TABLE SPLIT IS DELIBERATE ───────────────────────────────────────
 *
 * `nfl_player_match_scores` — per-league, carries the sabotage CARD bonus. This is what the
 * league actually scored and what the week-score header sums, so SAB / TRS / ANT come from
 * here and nowhere else.
 *
 * `nfl_player_game_stats` — global and league-independent. The only place the raw stat
 * categories exist, so YDS / TD / TO / SK / PEN and the per-position stat line come from
 * here, filtered to the same weeks.
 *
 * There is no single table with both. Do not collapse them: game_stats has no card bonus,
 * match_scores has no stats. They describe the same player-weeks at different grain, so
 * they reconcile per game without being the same rows.
 *
 * SAB-809 lives here too. In `nfl_player_match_scores` the column named `sabotage_points`
 * is the CARD BONUS, not the player total — the total is trash + anti + bonus. Reading the
 * column alone renders 0.0 for every unsabotaged player.
 *
 * Absence stays `null`, never 0: a player with no scored row in range renders "--". A
 * fabricated 0.0 is indistinguishable from a real zero and sorts as if it were real.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  EMPTY_RAW_STATS, addRawStats, readRawStats, type NflRawStats, type NflStatRow,
} from '@/components/nfl/nflStatsDefs';
import { NFL_STAT_SEASON_TYPE, resolveWeekRange, type NflRange } from '@/lib/nflWeekRange';
// SAB-1200: the ENGINE's re-score, imported rather than reimplemented. nfl-matchup-engine
// uses these same two functions for override leagues, so a week we compute here and a week
// the engine stored cannot drift apart by construction.
import {
  loadActiveSabotageModel, resolveLeagueModel, scoreStoredRow,
  type LoadedSabotageModel,
} from '@shared/nfl-sabotage-scoring.ts';

export interface UseNflStatRowsArgs {
  leagueId: string | null | undefined;
  seasonYear: number | null | undefined;
  /** The league's current week — resolves 'week' / 'last'. Not a clock. */
  currentWeek: number | null | undefined;
  range: NflRange;
  /** Restrict to a roster. `null` = every player (the Players surface). */
  playerIds: string[] | null;
  enabled?: boolean;
}

export interface NflStatRowsResult {
  rows: Map<string, NflStatRow>;
  /** True when the range covers no weeks — render an empty state, not a grid of zeros. */
  isEmptyRange: boolean;
  emptyReason: 'no-current-week' | 'no-prior-week' | null;
}

const emptyRow = (): NflStatRow => ({
  gamesPlayed: 0, raw: { ...EMPTY_RAW_STATS }, trash: null, anti: null, sab: null,
});

/**
 * SAB-1200: score one unowned week. Exported ONLY so a test can see WHICH MODEL is used.
 *
 * Inlining this in the hook made the choice untestable: a mutation swapping the resolved
 * league model for the raw global one passed every test, because the tests exercised the
 * shared scoring module directly and never the hook's wiring. A pure seam is the cheapest
 * way to make "we weighted it by the league" an assertable claim rather than a code-reading
 * one.
 */
export function scoreUnownedWeek(
  stats: Record<string, unknown>,
  resolved: { model: LoadedSabotageModel },
  ctx: { outcomeFinal: boolean; teamLost: boolean; position: string | null },
): { trash: number; anti: number } {
  return scoreStoredRow(stats as never, resolved.model, ctx);
}

export function useNflStatRows(args: UseNflStatRowsArgs) {
  const { leagueId, seasonYear, currentWeek, range, playerIds, enabled = true } = args;
  const window = resolveWeekRange(range, currentWeek);

  // playerIds is part of the key by CONTENT, not identity — a caller that rebuilds the array
  // each render must not refetch on every render.
  const rosterKey = playerIds ? [...playerIds].sort().join(',') : 'all';

  const query = useQuery<NflStatRowsResult>({
    queryKey: ['nfl-stat-rows', leagueId, seasonYear, range, window.weeks?.join(',') ?? 'season', rosterKey],
    enabled: enabled && !!leagueId && !!seasonYear && (playerIds === null || playerIds.length > 0),
    queryFn: async () => {
      // An empty range is a real answer, not a query. Returning early keeps a bye week from
      // costing two round trips to learn nothing.
      if (window.isEmpty) {
        return { rows: new Map(), isEmptyRange: true, emptyReason: window.emptyReason };
      }

      const rows = new Map<string, NflStatRow>();
      const at = (pid: string) => {
        let r = rows.get(pid);
        if (!r) { r = emptyRow(); rows.set(pid, r); }
        return r;
      };

      // ── raw stats ───────────────────────────────────────────────────────
      let statsQ = supabase
        .from('nfl_player_game_stats' as never)
        // SAB-1200: position / team_lost / outcome_final are the GameContext the engine
        // rebuilds for a stored row. Selected always -- they are three small columns and
        // branching the select on whether a fallback is needed costs a second round trip.
        .select('player_id, stats, week, position, team_lost, outcome_final')
        .eq('season_year', seasonYear as number)
        .eq('season_type', NFL_STAT_SEASON_TYPE);
      if (window.weeks) statsQ = (statsQ as never as { in: (c: string, v: number[]) => typeof statsQ }).in('week', window.weeks);
      if (playerIds) statsQ = (statsQ as never as { in: (c: string, v: string[]) => typeof statsQ }).in('player_id', playerIds);

      const { data: statRows, error: statErr } = await statsQ;
      if (statErr) throw statErr;

      // GP counts DISTINCT player-weeks, not rows. A player with two stat rows in one week
      // (a correction, a second ingest) must not read as two games — that would deflate
      // every Per Game number on the surface.
      const seenWeeks = new Map<string, Set<number>>();
      type StatRow = {
        player_id: string; stats: unknown; week: number;
        position: string | null; team_lost: boolean | null; outcome_final: boolean | null;
      };
      const statRowList = (statRows ?? []) as StatRow[];
      for (const s of statRowList) {
        const row = at(s.player_id);
        row.raw = addRawStats(row.raw, readRawStats(s.stats) as NflRawStats);
        let weeks = seenWeeks.get(s.player_id);
        if (!weeks) { weeks = new Set(); seenWeeks.set(s.player_id, weeks); }
        weeks.add(s.week);
      }
      for (const [pid, weeks] of seenWeeks) at(pid).gamesPlayed = weeks.size;

      // ── league-scored points ────────────────────────────────────────────
      let scoreQ = supabase
        .from('nfl_player_match_scores' as never)
        .select('player_id, trash_points, anti_points, sabotage_points, week')
        .eq('league_id', leagueId as string)
        .eq('season_year', seasonYear as number)
        .eq('season_type', NFL_STAT_SEASON_TYPE);
      if (window.weeks) scoreQ = (scoreQ as never as { in: (c: string, v: number[]) => typeof scoreQ }).in('week', window.weeks);
      if (playerIds) scoreQ = (scoreQ as never as { in: (c: string, v: string[]) => typeof scoreQ }).in('player_id', playerIds);

      const { data: scoreRows, error: scoreErr } = await scoreQ;
      if (scoreErr) throw scoreErr;

      const scoredPairs = new Set<string>();
      for (const s of (scoreRows ?? []) as {
        player_id: string; trash_points: number | null; anti_points: number | null;
        sabotage_points: number | null; week: number;
      }[]) {
        scoredPairs.add(`${s.player_id}:${s.week}`);
        const row = at(s.player_id);
        const trash = Number(s.trash_points) || 0;
        const anti = Number(s.anti_points) || 0;
        const bonus = Number(s.sabotage_points) || 0;
        row.trash = (row.trash ?? 0) + trash;
        row.anti = (row.anti ?? 0) + anti;
        // SAB-809: the total is the three-term sum. `sabotage_points` alone is the card bonus.
        row.sab = (row.sab ?? 0) + trash + anti + bonus;
      }

      // ── SAB-1200: weeks this league never scored ────────────────────────
      //
      // A player acquired AFTER a game has no nfl_player_match_scores row for that week --
      // the engine never wrote one, because he was not on a roster. His stat line still
      // renders (nfl_player_game_stats is global), so the row read as a real game beside
      // +0.0 and looked like broken data.
      //
      // THE RULE IS A DISPLAY RULE, NOT A SCORING RULE. A player's points always render in
      // HIS row, whoever owned him. Tenure (SAB-710) governs only whether they count toward
      // the TEAM TOTAL, which sums nfl_player_match_scores elsewhere and cannot see these
      // values -- different table, different query. Asserted in useNflStatRows.unowned.test.
      //
      // WEIGHTED, NOT GLOBAL. The stored global score would be right for seven of the eight
      // in-season leagues today and silently wrong for League of Trash, whose ten overrides
      // are mostly passing events -- so the first QB claimed post-kickoff there would diverge
      // with nothing signalling it. Re-scoring through the engine's own resolveLeagueModel +
      // scoreStoredRow means a week computed here and a week the engine stored agree by
      // construction, for every league, forever.
      const unscored = statRowList.filter(s => !scoredPairs.has(`${s.player_id}:${s.week}`));
      if (unscored.length > 0) {
        // Loaded ONLY when something is actually missing. A league with full coverage pays
        // nothing for this -- which is every league until someone claims a played player.
        const globalModel = await loadActiveSabotageModel(supabase);
        const { data: ovRow } = await supabase
          .from('nfl_scoring_settings' as never)
          .select('weights')
          .eq('league_id', leagueId as string)
          .maybeSingle();
        const resolved = resolveLeagueModel(
          globalModel,
          (ovRow as { weights?: unknown } | null)?.weights ?? null,
        );

        for (const s of unscored) {
          // SAB-1127: outcomeFinal gates the reversible (team-outcome) weights, and the
          // module documents that BOTH defaults are silently wrong on a money path --
          // true reinstates retreating scores, false strips the bonus from settled games.
          // So a row that cannot answer the question is SKIPPED rather than guessed: it
          // keeps its '--', which is honest, instead of a number built on a coin flip.
          // (Prod carries 0 nulls across 1289 rows today; this is the guard, not the case.)
          if (typeof s.outcome_final !== 'boolean') continue;
          const { trash, anti } = scoreUnownedWeek(
            (s.stats ?? {}) as Record<string, unknown>,
            resolved,
            {
              outcomeFinal: s.outcome_final,
              teamLost: s.team_lost === true,
              position: s.position ?? null,
            },
          );
          const row = at(s.player_id);
          row.trash = (row.trash ?? 0) + trash;
          row.anti = (row.anti ?? 0) + anti;
          // No card bonus term: a sabotage card is played BY a manager who owned him. An
          // unowned week has none by definition, so the total is the two-term sum here.
          row.sab = (row.sab ?? 0) + trash + anti;
        }
      }

      // Round once, at the end — summing pre-rounded values drifts.
      for (const row of rows.values()) {
        if (row.trash !== null) row.trash = Math.round(row.trash * 100) / 100;
        if (row.anti !== null) row.anti = Math.round(row.anti * 100) / 100;
        if (row.sab !== null) row.sab = Math.round(row.sab * 100) / 100;
      }

      return { rows, isEmptyRange: false, emptyReason: null };
    },
  });

  return {
    ...query,
    rows: query.data?.rows ?? new Map<string, NflStatRow>(),
    isEmptyRange: query.data?.isEmptyRange ?? window.isEmpty,
    emptyReason: query.data?.emptyReason ?? window.emptyReason,
  };
}
